import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BookingInfo } from "./BookingModal";
import { useNavigate } from "react-router-dom";
import heroImage from "../assets/images/hero-mountains.jpg";

interface Props {
  onBook: (info: BookingInfo) => void;
}

const stats = [
  { value: "500+", key: "hero.stats.destinations" },
  { value: "10K+", key: "hero.stats.happyTravelers" },
  { value: "1994", key: "hero.stats.established" },
  { value: "50+", key: "hero.stats.tourPackages" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" as const, delay: 0.08 * i },
  }),
};

const HeroSection: React.FC<Props> = ({ onBook }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div
      className="relative min-h-[92vh] bg-cover bg-center flex items-center"
      style={{
        backgroundImage: `url(${heroImage})`,
      }}
    >
      {/* Navy dusk overlay with a warm golden glow from the bottom */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/90 via-navy-950/70 to-navy-950/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-900/40 via-transparent to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 text-white w-full">
        <div className="max-w-3xl">
          <motion.span
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 border border-brand-400/40 bg-brand-500/10 backdrop-blur text-brand-300 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-[0.2em]"
          >
            <Sparkles size={14} />
            {t("hero.eyebrow")}
          </motion.span>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-serif text-4xl sm:text-6xl md:text-7xl font-bold leading-[1.5] mb-6"
          >
            {t("hero.titleLine1")}
            <br />
            <span className="inline-block pb-6 text-transparent bg-clip-text bg-gradient-to-r from-brand-300 via-brand-400 to-brand-500">
              {t("hero.titleLine2")}
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="text-lg sm:text-xl text-navy-100/90 mb-9 max-w-xl leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="flex flex-wrap gap-4"
          >
            <button
              onClick={() => onBook({ title: t("packages.cta.customTitle") })}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 font-bold px-8 py-4 rounded-full shadow-gold hover:-translate-y-0.5 hover:shadow-xl transition-all text-base sm:text-lg"
            >
              {t("hero.planTrip")}
              <ArrowRight size={19} />
            </button>
            <button
              onClick={() => navigate("/packages")}
              className="border-2 border-white/60 text-white font-bold px-8 py-4 rounded-full hover:bg-white hover:text-navy-950 transition-all text-base sm:text-lg"
            >
              {t("hero.viewPackages")}
            </button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-12 gap-y-6 mt-14"
          >
            {stats.map((stat) => (
              <div key={stat.key}>
                <div className="font-serif text-3xl sm:text-4xl font-bold text-brand-400">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-navy-200 uppercase tracking-wider mt-1">
                  {t(stat.key)}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
